"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  shortestSemitones,
  type Tonality,
} from "@/lib/music/tonality";
import { transposeKey } from "@/lib/music/key";
import {
  DONE_FLASH_MS,
  TRANSPOSE_RANGE,
  type ApplyState,
} from "./tonality-picker-helpers";

type UseTonalityPickerArgs = {
  tonality: Tonality | null;
  sourceTonality?: Tonality | null;
  onPick: (t: Tonality | null) => void;
  onTranspose?: (target: Tonality) => void | Promise<void>;
};

export type TonalityPickerState = {
  open: boolean;
  hasPiece: boolean;
  appliedSemis: number;
  stagedSemis: number;
  stagedTarget: Tonality | null;
  dirty: boolean;
  applyState: ApplyState;
  toggleOpen: () => void;
  closePopover: () => void;
  pickFree: (t: Tonality) => void;
  clearTonality: () => void;
  step: (delta: number) => void;
  setStagedSemis: (semis: number) => void;
  resetStaged: () => void;
  apply: () => Promise<void>;
};

export function useTonalityPicker({
  tonality,
  sourceTonality,
  onPick,
  onTranspose,
}: UseTonalityPickerArgs): TonalityPickerState {
  const [open, setOpen] = useState(false);
  const [stagedSemis, setStagedSemisInternal] = useState(0);
  const [applyState, setApplyState] = useState<ApplyState>("idle");

  const hasPiece = sourceTonality != null;

  const appliedSemis = useMemo(() => {
    if (!sourceTonality || !tonality) {
      return 0;
    }

    return shortestSemitones(sourceTonality, tonality);
  }, [sourceTonality, tonality]);

  const stagedTarget = useMemo<Tonality | null>(() => {
    if (!sourceTonality) {
      return null;
    }

    const result = transposeKey(
      sourceTonality.tonic,
      sourceTonality.mode,
      stagedSemis,
    );

    if (!result) {
      return sourceTonality;
    }

    return { tonic: result.tonic, mode: result.mode };
  }, [sourceTonality, stagedSemis]);

  const dirty = hasPiece && stagedSemis !== appliedSemis;

  // Reset staged when popover opens / applied tonality changes
  useEffect(() => {
    if (open) {
      setStagedSemisInternal(appliedSemis);
    }
  }, [open, appliedSemis]);

  // Auto-clear "done" flash
  useEffect(() => {
    if (applyState !== "done") {
      return;
    }

    const t = window.setTimeout(() => setApplyState("idle"), DONE_FLASH_MS);

    return () => window.clearTimeout(t);
  }, [applyState]);

  const toggleOpen = useCallback(() => setOpen((o) => !o), []);

  // Appelé par le `Popover`, qui possède désormais le clic extérieur et Échap.
  // Le hook les gérait aussi : deux fermetures concurrentes, c'est une de trop
  // à maintenir juste.
  const closePopover = useCallback(() => setOpen(false), []);

  const pickFree = useCallback(
    (t: Tonality) => {
      onPick(t);
      setOpen(false);
    },
    [onPick],
  );

  const clearTonality = useCallback(() => {
    onPick(null);
    setOpen(false);
  }, [onPick]);

  const step = useCallback((delta: number) => {
    setStagedSemisInternal((s) =>
      Math.max(-TRANSPOSE_RANGE, Math.min(TRANSPOSE_RANGE, s + delta)),
    );
  }, []);

  const setStagedSemis = useCallback((semis: number) => {
    setStagedSemisInternal(
      Math.max(-TRANSPOSE_RANGE, Math.min(TRANSPOSE_RANGE, semis)),
    );
  }, []);

  const resetStaged = useCallback(() => {
    setStagedSemisInternal(appliedSemis);
  }, [appliedSemis]);

  const apply = useCallback(async () => {
    if (!stagedTarget || !onTranspose) {
      return;
    }

    setApplyState("loading");
    try {
      await onTranspose(stagedTarget);
      setApplyState("done");
    } catch {
      setApplyState("idle");
    }
  }, [stagedTarget, onTranspose]);

  return {
    open,
    hasPiece,
    appliedSemis,
    stagedSemis,
    stagedTarget,
    dirty,
    applyState,
    toggleOpen,
    closePopover,
    pickFree,
    clearTonality,
    step,
    setStagedSemis,
    resetStaged,
    apply,
  };
}
